import { useState, useRef, useEffect } from "react";
import { Link } from "@remix-run/react";

interface AdminMenuProps {
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export default function AdminMenu({ isMobile = false, onLinkClick }: AdminMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      // Clear any pending close timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      // Add a small delay before closing the menu
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        closeTimeoutRef.current = null;
      }, 300); // 300ms delay gives enough time to move to submenu
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    if (isMobile) return;

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile]);

  return (
    <div 
      ref={menuRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative"
    >
      <button 
        onClick={toggleMenu}
        className={`flex items-center ${isMobile ? "w-full text-left text-white" : ""} hover:text-gray-700`}
      >
        Admin
        <svg 
          className="w-4 h-4 ml-1" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} 
          />
        </svg>
      </button>
      {isOpen && (
        <div className={
          isMobile 
            ? "pl-4 border-l border-primary-500 ml-2 mt-2 space-y-3" 
            : "absolute top-full left-0 bg-white text-gray-800 rounded shadow-lg py-2 mt-1 w-48 z-50 border border-gray-200"
        }>
          <Link 
            to="/admin/dashboard" 
            className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
            onClick={onLinkClick}
          >
            Admin Dashboard
          </Link>
          <Link 
            to="/admin/users" 
            className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
            onClick={onLinkClick}
          >
            User Management
          </Link>
          <Link 
            to="/admin/reports" 
            className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
            onClick={onLinkClick}
          >
            Reports
          </Link>
          <Link 
            to="/admin/performance" 
            className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
            onClick={onLinkClick}
          >
            Performance
          </Link>
          <Link 
            to="/admin/security-audit" 
            className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
            onClick={onLinkClick}
          >
            Security Audit
          </Link>
          <Link 
            to="/admin/db_management" 
            className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
            onClick={onLinkClick}
          >
            Database Management
          </Link>
          <Link 
            to="/admin/impersonate" 
            className={isMobile ? "block text-white hover:text-primary-200" : "block px-4 py-2 text-gray-800 hover:bg-gray-100 hover:text-gray-700"}
            onClick={onLinkClick}
          >
            Impersonate Role
          </Link>
        </div>
      )}
    </div>
  );
}
